import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { getValidationMessage, validateAll } from "@frui.ts/validation";
import { plainToClass } from "class-transformer";
import { action, computed, observable } from "mobx";
import { LicensePlateConfirmationValidationRules } from "models/orderItem";
import { CountryIdConfirmationValidationRules } from "models/vehicle";
import VehicleChangeDuringValidityRequest from "models/vehicleChangeDuringValidityRequest";
import { putChargeContactSuccess, putChargeVehicleSuccess } from "services/eventBus";
import { EntityValidationRules } from "services/validationService";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import ChargeManagementEditViewModel from "./chargeManagementEditViewModel";
import ChargeManagementLicensePlateChangeViewModel from "./chargeManagementLicensePlateChangeViewModel";
import React from "react";
import * as Sentry from "@sentry/react";
import { scrollToFirstError } from "../../../utils/scrollTo";
import NotificationPreferencesResponse from "entities/notificationPreferencesResponse";
import InputEmail from "entities/InputEmail";

export default class ChargeManagementActiveChargeLicensePlateChangeViewModel extends ChargeManagementLicensePlateChangeViewModel {
  readonly request: VehicleChangeDuringValidityRequest = new VehicleChangeDuringValidityRequest();
  navigationName = "vehicleChangeDuringValidity";

  @computed get agreementError() {
    return getValidationMessage(this.request, "_agreement");
  }

  @computed
  get charge() {
    return this.chargeManagementService.charge;
  }

  @computed
  get newVehicle() {
    return this.request;
  }

  get canSubmit() {
    return this.newVehicle?.licensePlate !== undefined || this.newVehicle?.countryId !== undefined;
  }

  @computed
  get hasSecondStep() {
    return true;
  }

  @computed
  get validationRules() {
    const validationRules: EntityValidationRules<VehicleChangeDuringValidityRequest> = {
      ...CountryIdConfirmationValidationRules,
      ...LicensePlateConfirmationValidationRules,
      countryId: {
        required: "requiredCountryId",
        manualValidation: this.manualValidator,
      },
      _agreement: {
        isTrue: true,
      },
      licensePlate: {
        hasRequiredLength: { min: 1, max: 10 },
        isLicensePlate: {
          hasLength: this.request.licensePlate.length,
        },
        licensePlateChange: {
          originalProperty: "_originalLicensePlate" as const,
        },
        manualValidation: this.manualValidator,
      },
    };

    validationRules._licensePlateConfirmation = {
      ...validationRules._licensePlateConfirmation,
      isLicensePlate: {
        hasLength: this.request.licensePlate.length,
      },
    };

    return validationRules;
  }

  protected onInitialize(): Promise<any> | void {
    if (this.charge) {
      this.request.licensePlate =
        this.request._originalLicensePlate =
        this.request._licensePlateConfirmation =
          this.charge.vehicle.licensePlate!;
    }
    this.newVehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.request, {
      attachValidator: false,
      fixedLicensePlateSize: true,
      context: "new",
      countryIDConfirmation: true,
      licensePlateConfirmation: true,
    });
    if (this.charge && !this.charge.emailAddress && this.charge) {
      this.emailInputViewModel = this.emailInputViewModelFactory(
        new InputEmail(this.charge.emailAddress, this.charge.notificationPreferences._emailConfirmation),
        "",
        ""
      );
    }
    attachAutomaticBlurValidator(this.request, this.validationRules);

    return super.onInitialize();
  }

  @bound
  protected async putChargeContactSuccess(data: ReturnType<typeof putChargeContactSuccess>) {
    // E-mail was filled from other part
    if (data.payload.triggerer !== "licensePlateChange" && data.payload.triggerer !== "contactChange") {
      this.emailInputViewModel = undefined;
      attachAutomaticBlurValidator(this.request, this.validationRules);
      this.request.emailAddress = undefined;
      return;
    } else {
      this.emailInputViewModel = this.emailInputViewModelFactory(
        new InputEmail(this.charge?.emailAddress, this.charge?.notificationPreferences._emailConfirmation),
        "",
        ""
      );
    }
  }

  @bound
  protected async putChargeVehicleSuccess(data: ReturnType<typeof putChargeVehicleSuccess>) {
    this.clearAfterAsyncAction();

    if (!this.charge) return;

    const { countryId, licensePlate, authCode, emailAddress } = data.payload.vehicle;

    this.success = true;
    this.charge.permittedActions.vehicleChangeDuringValidity.isAllowed = false;
    if (licensePlate) this.charge.vehicle.licensePlate = licensePlate;
    if (countryId) this.charge.vehicle.countryId = countryId;
    if (authCode) this.charge.authCode = authCode;
    if (emailAddress) this.charge.emailAddress = emailAddress;
    if (this.charge) {
      this.charge.notificationPreferences._emailConfirmation = emailAddress;
    }

    if (this.request.emailAddress) {
      this.charge.notificationPreferences.emailAddress = this.request.emailAddress;
      this.eventBus.publish(
        putChargeContactSuccess({
          emailAddress: this.request.emailAddress,
          notificationPreferences: plainToClass(NotificationPreferencesResponse, {
            emailAddress: this.request.emailAddress,
          }),
          triggerer: "licensePlateChange",
        })
      );
    }

    this.router.navigate(ChargeManagementEditViewModel, undefined, { countryId, licensePlate, authCode });
  }

  /*   onTurnstileVerify = async (turnstileToken: string) => {
    if (turnstileToken) {
      this.turnstileToken = turnstileToken;
    }
  }; */

  @action.bound
  @watchBusy
  async submit(turnstileToken?: string) {
    if (!this.showSecondStep) {
      await this.checkRsvValidity();
      this.showSecondStep = true;
    } else {
      this.sameVehicleCheck();
      this.success = false;
      this.errorMessage = undefined;
      this.secondStepErrorMessage = undefined;
      this.warningMessages = [];

      if (!this.validate()) {
        scrollToFirstError(this.formElement);
        return;
      }

      if (!turnstileToken || !this.turnstileViewModel.turnstileToken) return;
      const token = turnstileToken || this.turnstileViewModel.turnstileToken;

      this.isLoading = true;

      try {
        if (this.charge && !this.charge.emailAddress) {
          this.request.emailAddress = this.emailInputViewModel?.inputEmail.email;
          await this.chargeManagementService.editActiveCharge(this.request, token);
          return;
        }

        await this.chargeManagementService.editActiveCharge(this.request, token);
      } finally {
        this.isLoading = false;
      }
    }
  }

  @action.bound
  onCancelSecondStep() {
    this.request.countryId = undefined!;
    this.request._countryIdConfirmation = undefined!;
    this.request._agreement = false!;
    this.showSecondStep = false;
    this.hideConfirmChangeButton = false;
    this.request.licensePlate = this.charge?.vehicle.licensePlate!;
    this.request._licensePlateConfirmation = this.charge?.vehicle.licensePlate!;

    // We need to reset the Character Wise Input.
    // This is a hack to reset it, as changing value does nothing to the component.
    this.newVehicleInfoViewModel = undefined!;
    setTimeout(() => {
      this.newVehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.request, {
        attachValidator: false,
        fixedLicensePlateSize: true,
        context: "new",
        countryIDConfirmation: true,
        licensePlateConfirmation: true,
      });
    }, 100);

    attachAutomaticBlurValidator(this.request, this.validationRules);
  }

  @bound
  public validate() {
    const objectsForValidation: any[] = [this.request];
    if (this.charge && !this.charge.emailAddress) {
      objectsForValidation.push(this.emailInputViewModel?.inputEmail);
    }

    return validateAll(objectsForValidation);
  }
}
